import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	OptimizationIDModel,
	StatisticsInterpretabilityGroupedResponseModel,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'

import { any, getOr, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSelectedCurrency } from '@/entities/currency'
import {
	getFinishedAndLockedOptimizations,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatMoney, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'

export default function NumericGrossProfitImpactWidget({
	commonFilters,
	className,
}: {
	className?: string
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const optimizationsQuery = useOptimizationsQuery({
		select: getFinishedAndLockedOptimizations,
	})
	const currency = useSelectedCurrency()
	const revenueQuery = useInterpretabilityStatisticQuery(
		{
			currency,
			optimizations: optimizationsQuery.data as OptimizationIDModel[],
			filters: commonFilters || [],
		},
		{
			enabled: !!optimizationsQuery.data,
			select: calculateRevenueStatistics,
		}
	)
	const isLoading = any(prop('isLoading'), [revenueQuery, optimizationsQuery])
	const isError = any(prop('isError'), [revenueQuery, optimizationsQuery])

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.gross_profit_impact.title', {
					defaultValue: 'Gross Profit Impact',
				})}
				tooltip={t('widget.gross_profit_impact.tooltip', {
					defaultValue: 'widget.gross_profit_impact.tooltip',
				})}
				subtitle={t('widget.gross_profit_impact.description', {
					defaultValue: 'widget.gross_profit_impact.description',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				{isLoading && <Loader />}
				{!isLoading && (
					<div className='space-y-1'>
						<PercentDifferenceBadge
							value={revenueQuery?.data?.revenueDeltaPercent ?? 0}
						/>
						<p className='truncate text-3xl font-semibold'>
							{formatMoney(
								revenueQuery?.data?.revenue ?? 0,
								currency,
								NumberFormats.Compact
							)}
						</p>
					</div>
				)}
			</WidgetContent>
		</Widget>
	)
}
const Loader = () => (
	<div className='space-y-1'>
		<Skeleton className='col-span-2 h-4 w-32' />
		<Skeleton className='col-span-2 h-8 w-32' />
	</div>
)
const calculateRevenueStatistics = (
	data:
		| StatisticsInterpretabilityResponse
		| StatisticsInterpretabilityGroupedResponseModel[]
		| null
) => {
	if (!data) return { revenue: 0, revenueDeltaPercent: 0 }
	const finalRevenue = getOr(0, ['metrics', 'predict_gross_profit.final'], data)
	const initialRevenue = getOr(
		0,
		['metrics', 'predict_gross_profit.init'],
		data
	)
	const revenueDeltaPercent =
		((finalRevenue - initialRevenue) / initialRevenue) * 100

	return {
		revenue: finalRevenue,
		revenueDeltaPercent,
	}
}
